import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import ChatAttachmentDisplay from '@/components/dashboard/chat/ChatAttachmentDisplay';
import { CloseButton } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useChatFileUpload } from '@/hooks/useChatFileUpload';
import { registerChatView, unregisterChatView } from '@/hooks/useChatNotifications';
import { useClientGroupChat } from '@/hooks/useClientGroupChat';
import { useClientGroups, useSetGroupInboxState } from '@/hooks/useClientGroups';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUserRole } from '@/hooks/useUserRole';
import { ChatAttachment } from '@/models/GroupChatMessage';
import { getPublicUserProfile, getUserPresence, PublicUserProfile } from '@/services/api/users/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { ClientGroup, GroupMember } from '@/types/clientGroups';

const DARK = '#262626';
const ACCENT = '#c0ff02';
const T1 = 'var(--tx-text)';
const T2 = 'var(--tx-muted)';
const T3 = '#6b6766';
const BORDER = 'rgba(255,255,255,0.07)';
const _SHADOW_SM = '0 1px 4px rgba(0,0,0,0.10)';

const NAV_W = 56;
const PANEL_W = 280;

function Tip({
  label,
  placement = 'top',
  style,
  children,
}: {
  label: string;
  placement?: 'top' | 'left' | 'right';
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const computeStyle = (r: DOMRect): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'fixed',
      backgroundColor: 'var(--tx-glass-panel)',
      backgroundImage: 'var(--tx-surface-light)',
      boxShadow: 'var(--tx-shadow)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      color: T1,
      fontSize: 12,
      padding: '6px 10px',
      borderRadius: '12px',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 99999,
    };
    if (placement === 'right')
      return {
        ...base,
        top: r.top + r.height / 2,
        left: r.right + 8,
        transform: 'translateY(-50%)',
      };
    if (placement === 'left')
      return {
        ...base,
        top: r.top + r.height / 2,
        left: r.left - 8,
        transform: 'translate(-100%, -50%)',
      };
    return {
      ...base,
      top: r.top - 8,
      left: r.left + r.width / 2,
      transform: 'translate(-50%, -100%)',
    };
  };

  return (
    <div
      ref={ref}
      style={{ display: 'inline-flex', ...style }}
      onMouseEnter={() => setRect(ref.current?.getBoundingClientRect() ?? null)}
      onMouseLeave={() => setRect(null)}>
      {children}
      {rect && createPortal(<div style={computeStyle(rect)}>{label}</div>, document.body)}
    </div>
  );
}

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
};

const PRESENCE_TIMEOUT_MS = 5 * 60 * 1000;
const isOnline = (lastActiveAt?: string | null) => {
  if (!lastActiveAt) return false;
  const ts = new Date(lastActiveAt).getTime();
  return !Number.isNaN(ts) && Date.now() - ts < PRESENCE_TIMEOUT_MS;
};

interface AvatarProps {
  name: string;
  size?: number;
  photoUrl?: string;
  lastActiveAt?: string | null;
}
const Avatar = ({ name, size = 36, photoUrl, lastActiveAt }: AvatarProps) => {
  const ini = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const online = isOnline(lastActiveAt);
  const dotSize = Math.max(8, Math.round(size * 0.25));
  return (
    <div style={{ position: 'relative', flexShrink: 0, width: size, height: size }}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: '#ddd8cf',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.round(size * 0.38),
            fontWeight: 600,
            color: 'var(--tx-text)',
            fontFamily: 'Manrope,sans-serif',
          }}>
          {ini}
        </div>
      )}
      {online && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: ACCENT,
            border: '2px solid var(--tx-line)',
          }}
        />
      )}
    </div>
  );
};

// ─── Conversation list item ──────────────────────────────────────────────────
interface ConvoEntry {
  groupId: string;
  name: string;
  email?: string;
  phone?: string;
  photoUrl?: string;
  calLink?: string;
  lastActiveAt?: string | null;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  preview: string;
  time: string;
  unread: number;
  isActive: boolean;
  lastMsgAt: number;
}

// ─── Conversation Panel ───────────────────────────────────────────────────────
interface ClientPickerEntry {
  groupId: string;
  name: string;
  email?: string;
}

interface ConvoPanelProps {
  convos: ConvoEntry[];
  activeId: string;
  onSelect: (id: string) => void;
  onArchive: (groupId: string, isActive: boolean) => void;
  search: string;
  onSearch: (v: string) => void;
  tab: string;
  onTab: (t: string) => void;
  isMobile: boolean;
  isAdvisor: boolean;
  allClients: ClientPickerEntry[];
  onPickClient: (groupId: string) => void;
}

const ConversationPanel = ({
  convos,
  activeId,
  onSelect,
  onArchive,
  search,
  onSearch,
  tab,
  onTab,
  isMobile,
  isAdvisor,
  allClients,
  onPickClient,
}: ConvoPanelProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);
  const confirmConvo = confirmArchiveId ? convos.find((c) => c.groupId === confirmArchiveId) : null;
  const filtered = convos.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  const pickerFiltered = allClients.filter(
    (c) =>
      c.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
      (c.email || '').toLowerCase().includes(pickerSearch.toLowerCase()),
  );

  return (
    <div
      data-testid="inbox-conversation-panel"
      style={{
        position: 'fixed',
        top: isMobile ? 82 : 0,
        bottom: isMobile ? 12 : 0,
        left: isMobile ? 10 : NAV_W,
        right: isMobile ? 10 : undefined,
        width: isMobile ? undefined : PANEL_W,
        background: DARK,
        borderRight: isMobile ? 'none' : `1px solid ${BORDER}`,
        borderRadius: isMobile ? 25 : 0,
        overflow: isMobile ? 'hidden' : undefined,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1100,
        animation: 'chatPanelSlideIn 0.22s ease-out',
      }}>
      {/* Header */}
      <div
        style={{
          padding: isMobile ? '20px 20px 14px' : '18px 18px 14px',
          borderBottom: `1px solid ${BORDER}`,
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}>
          <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.02em', color: T1 }}>
            Messages
          </span>
          <div style={{ position: 'relative' }}>
            <button
              title="New chat"
              data-testid="inbox-new-chat-btn"
              onClick={() => {
                setShowPicker((p) => !p);
                setPickerSearch('');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                border: 0,
                borderRadius: 8,
                background: showPicker ? 'var(--tx-fill-2)' : 'transparent',
                cursor: 'pointer',
              }}>
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke={T2}
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>
            {showPicker && (
              <div
                style={{
                  position: 'absolute',
                  top: 36,
                  right: 0,
                  width: 244,
                  background: 'var(--tx-glass)',
                  borderRadius: '12px',
                  boxShadow: 'var(--tx-shadow-sm)',
                  zIndex: 10,
                  overflow: 'hidden',
                }}>
                <div style={{ padding: '10px 10px 6px' }}>
                  <input
                    autoFocus
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder={isAdvisor ? 'Search clients…' : 'Search advisors…'}
                    style={{
                      width: '100%',
                      background: 'var(--tx-fill)',
                      border: 0,
                      borderRadius: 8,
                      padding: '7px 10px',
                      fontSize: 12,
                      color: T1,
                      outline: 'none',
                      fontFamily: 'Manrope,sans-serif',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {pickerFiltered.length === 0 ? (
                    <div
                      style={{
                        padding: '14px 12px',
                        fontSize: 12,
                        color: T3,
                        textAlign: 'center',
                        fontFamily: 'Manrope,sans-serif',
                      }}>
                      {isAdvisor ? 'No clients found' : 'No advisors found'}
                    </div>
                  ) : (
                    pickerFiltered.map((c) => (
                      <div
                        key={c.groupId}
                        onClick={() => {
                          onPickClient(c.groupId);
                          setShowPicker(false);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          padding: '8px 12px',
                          cursor: 'pointer',
                          transition: 'background 0.1s',
                          fontFamily: 'Manrope,sans-serif',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(255,255,255,0.06)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                        }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T1 }}>{c.name}</span>
                        {c.email && <span style={{ fontSize: 11, color: T3 }}>{c.email}</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--tx-fill)',
            borderRadius: 10,
            padding: '8px 12px',
            border: 0,
          }}>
          <svg
            width={13}
            height={13}
            viewBox="0 0 24 24"
            fill="none"
            stroke={T3}
            strokeWidth={2}
            strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search"
            data-testid="inbox-search-input"
            style={{
              fontSize: 12,
              color: T1,
              width: '100%',
              outline: 'none',
              border: 'none',
              background: 'transparent',
              fontFamily: 'Manrope,sans-serif',
            }}
          />
        </div>

        {/* Open / Closed */}
        <div
          style={{
            display: 'flex',
            marginTop: 10,
            background: 'var(--tx-fill)',
            borderRadius: 10,
            overflow: 'hidden',
          }}>
          {['Open', 'Archived'].map((t) => (
            <button
              key={t}
              data-testid={`inbox-tab-${t.toLowerCase()}`}
              onClick={() => onTab(t)}
              style={{
                flex: 1,
                padding: '7px 0',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                color: tab === t ? T1 : T2,
                background: tab === t ? 'var(--tx-fill-2)' : 'transparent',
                border: 'none',
                fontFamily: 'Manrope,sans-serif',
                transition: 'all 0.12s',
              }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        {filtered.length === 0 && (
          <div
            data-testid="inbox-empty-state"
            style={{
              padding: '40px 18px',
              textAlign: 'center',
              color: T3,
              fontSize: 13,
              fontFamily: 'Manrope,sans-serif',
            }}>
            No conversations
          </div>
        )}
        {filtered.map((c) => (
          <div
            key={c.groupId}
            data-testid={`inbox-conversation-${c.groupId}`}
            onClick={() => onSelect(c.groupId)}
            onMouseEnter={() => setHoveredId(c.groupId)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: isMobile ? '14px 20px' : '14px 18px',
              cursor: 'pointer',
              borderLeft: c.groupId === activeId ? `2px solid ${ACCENT}` : '2px solid transparent',
              background: c.groupId === activeId ? 'var(--tx-fill)' : 'transparent',
              transition: 'all 0.1s',
              userSelect: 'none',
            }}>
            <Avatar name={c.name} size={34} photoUrl={c.photoUrl} lastActiveAt={c.lastActiveAt} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: c.unread ? 700 : 500,
                    color: T1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {c.name}
                </span>
                <span style={{ fontSize: 10, color: T3, flexShrink: 0 }}>{c.time}</span>
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 11,
                    color: T2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 150,
                  }}>
                  {c.preview}
                </span>
                <div style={{ position: 'relative', width: 20, height: 16, flexShrink: 0 }}>
                  {c.unread > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        inset: 0,
                        minWidth: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: ACCENT,
                        color: DARK,
                        fontSize: 10,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 3px',
                        opacity: hoveredId === c.groupId ? 0 : 1,
                        transition: 'opacity 0.1s',
                      }}>
                      {c.unread}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmArchiveId(c.groupId);
                    }}
                    title={c.isActive ? 'Archive' : 'Unarchive'}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: hoveredId === c.groupId ? 0.6 : 0,
                      transition: 'opacity 0.1s',
                    }}>
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={T2}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      {c.isActive ? (
                        <>
                          <polyline points="21 8 21 21 3 21 3 8" />
                          <rect x="1" y="3" width="22" height="5" />
                          <line x1="10" y1="12" x2="14" y2="12" />
                        </>
                      ) : (
                        <>
                          <polyline points="21 8 21 21 3 21 3 8" />
                          <rect x="1" y="3" width="22" height="5" />
                          <polyline points="10 12 12 10 14 12" />
                          <line x1="12" y1="10" x2="12" y2="16" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* Archive confirm dialog */}
        {confirmConvo && (
          <div
            onClick={() => setConfirmArchiveId(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--tx-backdrop)',
            }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '16px',
                padding: '24px 28px',
                width: 320,
                fontFamily: 'Manrope,sans-serif',
              }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T1, marginBottom: 8 }}>
                {confirmConvo.isActive ? 'Archive conversation?' : 'Unarchive conversation?'}
              </div>
              <div style={{ fontSize: 13, color: T2, marginBottom: 20 }}>
                {confirmConvo.isActive
                  ? 'This conversation will be moved to Archived. You can restore it at any time.'
                  : 'This conversation will be moved back to Open.'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  onClick={() => setConfirmArchiveId(null)}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 8,
                    color: T2,
                    fontSize: 13,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onArchive(confirmConvo.groupId, confirmConvo.isActive);
                    setConfirmArchiveId(null);
                  }}
                  style={{
                    background: ACCENT,
                    border: 'none',
                    borderRadius: 8,
                    color: DARK,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}>
                  {confirmConvo.isActive ? 'Archive' : 'Unarchive'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Chat View (message thread + compose) ─────────────────────────────────────
interface ChatViewProps {
  groupId: string;
  name: string;
  email?: string;
  phone?: string;
  photoUrl?: string;
  calLink?: string;
  lastActiveAt?: string | null;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  isActive: boolean;
  isMobile: boolean;
  panelVisible: boolean;
  members?: GroupMember[];
  group?: ClientGroup;
  onBack: () => void;
}

const ChatView = ({
  groupId,
  name,
  email,
  phone,
  photoUrl,
  calLink,
  lastActiveAt,
  address,
  city,
  state,
  zip,
  isMobile,
  panelVisible,
  members = [],
  group,
  onBack,
}: ChatViewProps) => {
  const { user } = useAuthContext();
  const { isAdvisor: viewerIsAdvisor } = useUserRole();

  // Build per-sender advisor profile map (primary + co-advisors)
  const allAdvisorIds = useMemo(() => {
    if (!group) return [] as string[];
    const ids = new Set<string>([group.advisorId, ...(group.coAdvisorIds ?? [])]);
    return [...ids];
  }, [group]);

  const advisorProfileResults = useQueries({
    queries: allAdvisorIds.map((id) => ({
      queryKey: ['public-profile', id],
      queryFn: () => getPublicUserProfile(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const advisorProfileMap = useMemo(() => {
    const map: Record<string, { name: string; photoUrl?: string }> = {};
    allAdvisorIds.forEach((id, i) => {
      const data = advisorProfileResults[i]?.data;
      if (data) map[id] = { name: data.name || 'Advisor', photoUrl: data.photoUrl };
    });
    return map;
  }, [allAdvisorIds, advisorProfileResults]);
  const { showAlert } = useSnackbar();
  const navigate = useNavigate();
  const t = useTranslationPrefix('toasts');
  const contactRole = viewerIsAdvisor ? 'Client' : 'Advisor';
  const {
    messages,
    sendMessage,
    sendDeleteMessage,
    sendEditMessage,
    sendReaction,
    markAsRead,
    isLoadingMessages,
    hasMoreMessages,
    loadMoreMessages,
    isFetchingMore,
  } = useClientGroupChat(groupId);

  const {
    startUpload,
    validateFiles,
    uploadProgress,
    inProgress: isUploading,
    clearUploadProgress,
  } = useChatFileUpload(groupId);

  const [input, setInput] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [stagedAttachments, setStagedAttachments] = useState<ChatAttachment[]>([]);
  const [stagedPreviews, setStagedPreviews] = useState<{ file: File; objectUrl: string }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);
  const [_hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);
  const [actionBarMsgId, setActionBarMsgId] = useState<string | null>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const touchHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const actionBarOpenedAtRef = useRef<number>(0);
  const [editingCreatedAt, setEditingCreatedAt] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');

  // Dismiss action bar when clicking outside it
  useEffect(() => {
    if (!actionBarMsgId) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      // Ignore events fired immediately after long-press open (browser simulates mousedown on touchend)
      if (Date.now() - actionBarOpenedAtRef.current < 300) return;
      if (actionBarRef.current && !actionBarRef.current.contains(e.target as Node)) {
        setActionBarMsgId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [actionBarMsgId]);

  const handleMessagesScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  }, []);

  // Register this chat view for notification suppression
  useEffect(() => {
    registerChatView(groupId);
    markAsRead();
    return () => unregisterChatView(groupId);
  }, [groupId]);

  // Auto-scroll on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Mark as read when messages arrive while viewing
  useEffect(() => {
    if (messages.length > 0) {
      markAsRead();
    }
  }, [messages.length]);

  // Clean up object URLs on unmount
  const stagedPreviewsRef = useRef(stagedPreviews);
  stagedPreviewsRef.current = stagedPreviews;
  useEffect(
    () => () => {
      stagedPreviewsRef.current.forEach((p) => {
        if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
      });
    },
    [],
  );

  const insertEmoji = (emoji: string) => {
    setInput((prev) => prev + emoji);
    setEmojiOpen(false);
    textareaRef.current?.focus();
  };

  const send = () => {
    if (isUploading) return;
    if (!input.trim() && stagedAttachments.length === 0) return;
    sendMessage(
      input.trim(),
      undefined,
      stagedAttachments.length > 0 ? stagedAttachments : undefined,
    );
    setInput('');
    stagedPreviews.forEach((p) => {
      if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
    });
    setStagedAttachments([]);
    setStagedPreviews([]);
    clearUploadProgress();
  };

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (isUploading) return;
      const error = validateFiles(files);
      if (error) {
        console.error('File validation failed', error);
        showAlert(t(error.messageKey, error.messageParams), 'error');
        return;
      }
      const previews = files.map((file) => ({
        file,
        objectUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      }));
      setStagedPreviews((prev) => {
        prev.forEach((p) => {
          if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
        });
        return previews;
      });
      const attachments = await startUpload(files);
      if (attachments) {
        setStagedAttachments(attachments);
      } else {
        previews.forEach((p) => {
          if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
        });
        setStagedPreviews([]);
        setStagedAttachments([]);
        clearUploadProgress();
        showAlert(t('file.upload_failed'), 'error');
      }
    },
    [validateFiles, startUpload, showAlert, isUploading],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) handleFilesSelected(files);
      e.target.value = '';
    },
    [handleFilesSelected],
  );

  const removeStagedFile = useCallback((index: number) => {
    setStagedPreviews((prev) => {
      const removed = prev[index];
      if (removed?.objectUrl) URL.revokeObjectURL(removed.objectUrl);
      return prev.filter((_, i) => i !== index);
    });
    setStagedAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFilesSelected(files);
    },
    [handleFilesSelected],
  );

  const midnightOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  const yesterdayMidnight = (now: Date) =>
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const formatDateSeparator = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    if (midnightOf(d) === midnightOf(now)) return 'Today';
    if (midnightOf(d) === yesterdayMidnight(now)) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });
  };

  // Group messages by date for separators
  const messagesWithDates = useMemo(() => {
    const result: { type: 'date' | 'message'; date?: string; message?: (typeof messages)[0] }[] =
      [];
    let lastDate = '';
    for (const msg of messages) {
      const date = formatDateSeparator(msg.createdAt);
      if (date !== lastDate) {
        result.push({ type: 'date', date });
        lastDate = date;
      }
      result.push({ type: 'message', message: msg });
    }
    return result;
  }, [messages]);

  return (
    <div
      data-testid="inbox-chat-view"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flex: 1,
        minWidth: 0,
        position: 'relative',
        marginLeft: isMobile ? 0 : panelVisible ? PANEL_W + 20 : 0,
        paddingLeft: isMobile ? 10 : 0,
        paddingTop: isMobile ? 12 : 20,
        paddingRight: 10,
        paddingBottom: 12,
        fontFamily: 'Manrope,sans-serif',
        color: T1,
        boxSizing: 'border-box',
        animation: isMobile ? 'chatViewSlideIn 0.2s ease-out' : undefined,
        gap: isMobile ? 0 : 12,
      }}>
      <div
        style={
          isMobile
            ? {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                background: DARK,
                borderRadius: 25,
                overflow: 'hidden',
              }
            : {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                minHeight: 0,
                overflow: 'hidden',
              }
        }>
        {/* Top bar */}
        <div
          style={{
            background: isMobile ? undefined : DARK,
            borderRadius: isMobile ? 0 : 16,
            borderBottom: isMobile ? `1px solid ${BORDER}` : undefined,
            padding: isMobile ? '16px 20px' : '18px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            flexWrap: 'nowrap',
            minWidth: 0,
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 10 : 12,
              minWidth: 0,
              overflow: 'hidden',
            }}>
            {isMobile && (
              <button
                onClick={onBack}
                data-testid="inbox-back-btn"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px 0 0',
                  flexShrink: 0,
                }}>
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T2}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}
            <Avatar
              name={name}
              size={isMobile ? 36 : 42}
              photoUrl={photoUrl}
              lastActiveAt={lastActiveAt}
            />
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 11 }}>
                <span
                  style={{
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: T1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {name}
                </span>
                {!isMobile && (
                  <Tip label="Contact info" placement="right" style={{ alignSelf: 'flex-end' }}>
                    <button
                      onClick={() => setInfoOpen(true)}
                      data-testid="inbox-info-btn"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: T3,
                        flexShrink: 0,
                        transition: 'color 0.15s',
                        marginBottom: 2,
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.color = T2;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.color = T3;
                      }}>
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.75}
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <circle cx="8" cy="11" r="2.5" />
                        <path d="M4 18c0-2 1.8-3 4-3s4 1 4 3" />
                        <line x1="15" y1="9" x2="20" y2="9" />
                        <line x1="15" y1="13" x2="20" y2="13" />
                      </svg>
                    </button>
                  </Tip>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: T2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {contactRole}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 4 : 8, flexShrink: 0 }}>
            {isMobile && (
              <Tip label="Contact info" placement="left">
                <button
                  onClick={() => setInfoOpen(true)}
                  data-testid="inbox-info-btn"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    background: 'var(--tx-fill)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 0,
                  }}>
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T2}
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <circle cx="8" cy="11" r="2.5" />
                    <path d="M4 18c0-2 1.8-3 4-3s4 1 4 3" />
                    <line x1="15" y1="9" x2="20" y2="9" />
                    <line x1="15" y1="13" x2="20" y2="13" />
                  </svg>
                </button>
              </Tip>
            )}
            <Tip label="Go to clients" placement="left">
              <button
                onClick={() => navigate('/dashboard/clients')}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '9px',
                  background: 'var(--tx-fill)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 0,
                }}>
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T2}
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </button>
            </Tip>
          </div>

          {/* Info dialog */}
          {infoOpen && (
            <div
              onClick={() => setInfoOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                background: 'var(--tx-backdrop)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 16px',
              }}>
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'var(--tx-glass-panel)',
                  backgroundImage: 'var(--tx-surface-light)',
                  boxShadow: 'var(--tx-shadow)',
                  borderRadius: '24px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: 280,
                  fontFamily: 'Manrope,sans-serif',
                }}>
                {/* Close button — top right */}
                <CloseButton
                  size="sm"
                  onClose={() => setInfoOpen(false)}
                  label="Close"
                  sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
                />

                {/* Role badge — top right */}
                <div
                  style={{
                    position: 'absolute',
                    top: 16,
                    right: 44,
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--tx-muted)',
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 8px',
                  }}>
                  {contactRole}
                </div>

                {/* Hero: avatar + name */}
                <div
                  style={{
                    padding: '28px 22px 10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}>
                  <Avatar name={name} size={62} photoUrl={photoUrl} />
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 600,
                      color: 'var(--tx-text)',
                      marginTop: 14,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}>
                    {name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>{contactRole}</div>
                </div>

                {/* Contact info */}
                <div
                  style={{
                    padding: '6px 22px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}>
                  {email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8b8680"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}>
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <polyline points="22,4 12,13 2,4" />
                      </svg>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--tx-faint)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}>
                        {email}
                      </div>
                    </div>
                  )}
                  {phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8b8680"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}>
                        {/* eslint-disable-next-line max-len */}
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                      </svg>
                      <div style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{formatPhone(phone)}</div>
                    </div>
                  )}
                  {(address || city) && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8b8680"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0, marginTop: 1 }}>
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                      <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.5 }}>
                        {address && <div>{address}</div>}
                        {(city || state || zip) && (
                          <div>
                            {[city, state].filter(Boolean).join(', ')}
                            {zip ? ` ${zip}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {calLink && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8b8680"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <a
                        href={`https://cal.com/${calLink}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--tx-faint)', textDecoration: 'none' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--tx-text)')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--tx-faint)')}>
                        Book a meeting
                      </a>
                    </div>
                  )}
                </div>

                <div style={{ height: 16 }} />
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
          {showScrollDown && (
            <button
              onClick={() => endRef.current?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: 'var(--tx-shadow)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <svg
                width={14}
                height={14}
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f3f3f3"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
          <div
            ref={scrollContainerRef}
            className="hc-scroll"
            onScroll={handleMessagesScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '4px 20px' : '4px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}>
            {/* Load more */}
            {hasMoreMessages && (
              <div style={{ textAlign: 'center', paddingTop: 8 }}>
                <button
                  data-testid="inbox-load-more-btn"
                  onClick={() => loadMoreMessages()}
                  disabled={isFetchingMore}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 8,
                    padding: '6px 16px',
                    color: T2,
                    fontSize: 12,
                    fontFamily: 'Manrope,sans-serif',
                    cursor: 'pointer',
                  }}>
                  {isFetchingMore ? 'Loading...' : 'Load earlier messages'}
                </button>
              </div>
            )}

            {isLoadingMessages && (
              <div style={{ textAlign: 'center', paddingTop: 40, color: T3, fontSize: 13 }}>
                Loading messages...
              </div>
            )}

            {!isLoadingMessages && messages.length === 0 && (
              <div style={{ textAlign: 'center', paddingTop: 40, color: T3, fontSize: 13 }}>
                No messages yet. Say hello!
              </div>
            )}

            {messagesWithDates.map((item, idx) => {
              if (item.type === 'date') {
                return (
                  <div key={`date-${idx}`} style={{ textAlign: 'center', paddingTop: 8 }}>
                    <span style={{ fontSize: 12, color: '#4a4a4a' }}>
                      <strong>{item.date}</strong>
                    </span>
                  </div>
                );
              }

              if (!item.message) return null;
              const msg = item.message;
              const isMe = msg.userId === user?.id;
              const isDeleted = msg.status === 'deleted';

              if (isDeleted) return null;

              const isEditing = editingCreatedAt === msg.createdAt;

              // iOS-style clustering
              const prevItem = messagesWithDates[idx - 1];
              const nextItem = messagesWithDates[idx + 1];
              const prevMsg = prevItem?.type === 'message' ? prevItem.message : null;
              const nextMsg = nextItem?.type === 'message' ? nextItem.message : null;
              const isGrouped = prevMsg && prevMsg.userId === msg.userId;
              const nextIsGrouped = nextMsg && nextMsg.userId === msg.userId;
              const showName = !isMe && !isGrouped;
              const showAvatar = !isMe && !nextIsGrouped;

              // Resolve correct photo per sender
              const isSenderAdvisor = allAdvisorIds.includes(msg.userId);
              const msgPhotoUrl = viewerIsAdvisor
                ? isSenderAdvisor
                  ? advisorProfileMap[msg.userId]?.photoUrl
                  : members.find((m) => m.userId === msg.userId)?.photoUrl
                : advisorProfileMap[msg.userId]?.photoUrl ?? photoUrl;

              return (
                <div
                  key={msg.messageId}
                  data-testid={`inbox-message-${msg.messageId}`}
                  tabIndex={0}
                  onMouseEnter={() => setHoveredMsgId(msg.messageId)}
                  onMouseLeave={() => setHoveredMsgId(null)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setActionBarMsgId((prev) => (prev === msg.messageId ? null : msg.messageId));
                  }}
                  onTouchStart={() => {
                    touchHoldTimerRef.current = setTimeout(() => {
                      actionBarOpenedAtRef.current = Date.now();
                      setActionBarMsgId(msg.messageId);
                    }, 500);
                  }}
                  onTouchEnd={() => {
                    if (touchHoldTimerRef.current) {
                      clearTimeout(touchHoldTimerRef.current);
                      touchHoldTimerRef.current = null;
                    }
                  }}
                  onTouchMove={() => {
                    if (touchHoldTimerRef.current) {
                      clearTimeout(touchHoldTimerRef.current);
                      touchHoldTimerRef.current = null;
                    }
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                    marginTop: isGrouped ? 2 : 12,
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isMe ? 'row-reverse' : 'row',
                      gap: 10,
                      alignItems: 'flex-end',
                      width: '100%',
                    }}>
                    {!isMe &&
                      (showAvatar ? (
                        <Avatar name={msg.senderName || name} size={32} photoUrl={msgPhotoUrl} />
                      ) : (
                        <div style={{ width: 32, flexShrink: 0 }} />
                      ))}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isMe ? 'flex-end' : 'flex-start',
                        gap: 5,
                        maxWidth: 'calc(100% - 40px)',
                        minWidth: 0,
                      }}>
                      {showName && (
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--tx-faint)',
                            fontWeight: 600,
                          }}>
                          {msg.senderName}
                        </span>
                      )}
                      {isEditing ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            width: '100%',
                          }}>
                          <textarea
                            className="hc-input"
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (editDraft.trim())
                                  sendEditMessage(msg.createdAt, editDraft.trim());
                                setEditingCreatedAt(null);
                              }
                              if (e.key === 'Escape') setEditingCreatedAt(null);
                            }}
                            autoFocus
                            rows={1}
                            style={{
                              fontSize: 13,
                              color: 'var(--tx-text)',
                              lineHeight: 1.6,
                              fontFamily: 'Manrope,sans-serif',
                              padding: '10px 14px',
                              borderRadius: '12px',
                              background: 'var(--tx-glass-card)',
                              border: 0,
                              resize: 'none',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setEditingCreatedAt(null)}
                              data-testid="inbox-edit-cancel-btn"
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                border: 'none',
                                background: 'var(--tx-fill-3)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                              <svg
                                width={14}
                                height={14}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#f3f3f3"
                                strokeWidth={2.5}
                                strokeLinecap="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                if (editDraft.trim())
                                  sendEditMessage(msg.createdAt, editDraft.trim());
                                setEditingCreatedAt(null);
                              }}
                              data-testid="inbox-edit-save-btn"
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                border: 'none',
                                background: ACCENT,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                              <svg
                                width={14}
                                height={14}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={DARK}
                                strokeWidth={2.5}
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            position: 'relative',
                            paddingTop: msg.reactions && msg.reactions.length > 0 ? 14 : 0,
                          }}>
                          {/* Action bar — triggered by right-click (desktop) or long-press (mobile) */}
                          {actionBarMsgId === msg.messageId && !isEditing && (
                            <div
                              ref={actionBarRef}
                              style={{
                                position: 'absolute',
                                bottom: 'calc(100% + 6px)',
                                ...(isMe ? { right: 0 } : { left: 0 }),
                                display: 'flex',
                                gap: 4,
                                alignItems: 'center',
                                background: 'var(--tx-fill-3)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                borderRadius: 10,
                                padding: '4px 6px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                zIndex: 10,
                                whiteSpace: 'nowrap',
                              }}>
                              {['👍', '❤️', '😂', '😢', '👏'].map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => {
                                    sendReaction(msg.createdAt, emoji, 'add');
                                    setActionBarMsgId(null);
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '2px 4px',
                                    fontSize: 17,
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                    transition: 'transform 0.1s',
                                  }}
                                  onMouseEnter={(e) =>
                                    (e.currentTarget.style.transform = 'scale(1.25)')
                                  }
                                  onMouseLeave={(e) =>
                                    (e.currentTarget.style.transform = 'scale(1)')
                                  }>
                                  {emoji}
                                </button>
                              ))}
                              {isMe && (
                                <>
                                  <div
                                    style={{
                                      width: 1,
                                      height: 18,
                                      background: 'var(--tx-fill)',
                                      margin: '0 2px',
                                    }}
                                  />
                                  <button
                                    onClick={() => {
                                      setEditingCreatedAt(msg.createdAt);
                                      setEditDraft(msg.content);
                                      setActionBarMsgId(null);
                                    }}
                                    title="Edit"
                                    data-testid="inbox-edit-msg-btn"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 26,
                                      height: 26,
                                      background: 'transparent',
                                      border: 'none',
                                      borderRadius: 6,
                                      cursor: 'pointer',
                                    }}>
                                    <svg
                                      width={13}
                                      height={13}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#333333"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => {
                                      sendDeleteMessage(msg.createdAt);
                                      setActionBarMsgId(null);
                                    }}
                                    title="Delete"
                                    data-testid="inbox-delete-msg-btn"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 26,
                                      height: 26,
                                      background: 'transparent',
                                      border: 'none',
                                      borderRadius: 6,
                                      cursor: 'pointer',
                                    }}>
                                    <svg
                                      width={13}
                                      height={13}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#333333"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                      <path d="M10 11v6M14 11v6" />
                                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          <div
                            style={{
                              padding: '10px 14px',
                              borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                              background: isMe ? 'var(--tx-glass-card)' : 'var(--tx-fill)',
                              fontSize: 13,
                              lineHeight: 1.5,
                              color: isMe ? 'var(--tx-text)' : T1,
                              whiteSpace: 'pre-wrap',
                              opacity: msg.status === 'pending' ? 0.6 : 1,
                            }}>
                            {msg.content}
                            {msg.edited && (
                              <span
                                style={{ fontSize: 10, color: isMe ? '#888' : T3, marginLeft: 6 }}>
                                (edited)
                              </span>
                            )}
                            {msg.attachments?.map((att) => (
                              <ChatAttachmentDisplay
                                key={att.s3Key}
                                attachment={att}
                                groupId={msg.groupId}
                                isOwn={isMe}
                              />
                            ))}
                          </div>
                          {/* Reactions — iMessage style, overlapping bottom corner */}
                          {msg.reactions && msg.reactions.length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 2,
                                ...(isMe ? { left: -18 } : { right: -18 }),
                                display: 'flex',
                                gap: 1,
                                background: '#3c3c3e',
                                borderRadius: '12px',
                                padding: '2px 5px',
                                border: '1.5px solid var(--tx-line)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                                zIndex: 2,
                              }}>
                              {Object.entries(
                                msg.reactions.reduce((acc, r) => {
                                  acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                  return acc;
                                }, {} as Record<string, number>),
                              ).map(([emoji, count]) => {
                                const hasMyReaction = msg.reactions?.some(
                                  (r) => r.userId === user?.id && r.emoji === emoji,
                                );
                                return (
                                  <button
                                    key={emoji}
                                    onClick={() =>
                                      sendReaction(
                                        msg.createdAt,
                                        emoji,
                                        hasMyReaction ? 'remove' : 'add',
                                      )
                                    }
                                    aria-label={`${
                                      hasMyReaction ? 'Remove' : 'Add'
                                    } ${emoji} reaction`}
                                    aria-pressed={hasMyReaction}
                                    style={{
                                      fontSize: 13,
                                      background: 'transparent',
                                      border: 'none',
                                      padding: '0 2px',
                                      cursor: 'pointer',
                                      lineHeight: 1.4,
                                      fontFamily: 'inherit',
                                      color: hasMyReaction ? ACCENT : T1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                    }}>
                                    {emoji}
                                    {count > 1 && (
                                      <span style={{ fontSize: 10, fontWeight: 600 }}>{count}</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <span
                    style={{ fontSize: 10, color: T3, marginTop: 2, paddingLeft: !isMe ? 42 : 0 }}>
                    {formatTime(msg.createdAt)}
                  </span>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        </div>

        {/* Compose */}
        <div
          style={{
            background: isMobile ? undefined : DARK,
            borderRadius: isMobile ? 0 : 16,
            flexShrink: 0,
          }}>
          {/* Staged attachments — inside compose box */}
          {stagedPreviews.length > 0 && (
            <div
              style={{
                padding: isMobile ? '12px 20px 0' : '12px 18px 0',
                borderBottom: `1px solid ${BORDER}`,
              }}>
              {isUploading && (
                <div
                  style={{
                    height: 3,
                    borderRadius: 2,
                    background: 'var(--tx-fill-2)',
                    marginBottom: 8,
                    overflow: 'hidden',
                  }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${uploadProgress}%`,
                      background: ACCENT,
                      borderRadius: 2,
                      transition: 'width 0.2s',
                    }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 12 }}>
                {stagedPreviews.map((preview, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 8px',
                      borderRadius: 8,
                      border: 0,
                      background: 'var(--tx-fill)',
                      maxWidth: 200,
                    }}>
                    {preview.objectUrl ? (
                      <img
                        src={preview.objectUrl}
                        alt={preview.file.name}
                        style={{
                          width: 20,
                          height: 20,
                          objectFit: 'cover',
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <svg
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="rgba(243,243,243,0.5)"
                        strokeWidth={1.5}
                        style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--tx-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        fontFamily: 'Manrope,sans-serif',
                      }}>
                      {preview.file.name}
                    </span>
                    <button
                      onClick={() => removeStagedFile(index)}
                      disabled={isUploading}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}>
                      <svg
                        width={12}
                        height={12}
                        viewBox="0 0 24 24"
                        stroke="rgba(243,243,243,0.4)"
                        strokeWidth={2.5}>
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div
            style={{
              padding: isMobile ? '14px 20px 10px' : '14px 18px 10px',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 12,
            }}>
            <textarea
              ref={textareaRef}
              className="hc-input"
              data-testid="inbox-message-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Write your message..."
              rows={1}
              style={{
                flex: 1,
                fontSize: 13,
                color: T1,
                lineHeight: 1.6,
                fontFamily: 'Manrope,sans-serif',
                maxHeight: 120,
                overflowY: 'auto',
                resize: 'none',
              }}
            />
            <button
              onClick={send}
              disabled={isUploading}
              data-testid="inbox-send-btn"
              style={{ marginBottom: 2, cursor: 'pointer', background: 'none', border: 'none' }}>
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke={input.trim() || stagedAttachments.length > 0 ? ACCENT : T3}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '8px 20px 14px' : '8px 18px 14px',
              borderTop: `1px solid ${BORDER}`,
            }}>
            <div style={{ display: 'flex', gap: isMobile ? 14 : 18, position: 'relative' }}>
              {/* Emoji */}
              <button
                onClick={() => setEmojiOpen((o) => !o)}
                data-testid="inbox-emoji-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 0 : 5,
                  fontSize: 12,
                  color: T2,
                  fontFamily: 'Manrope,sans-serif',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}>
                <svg
                  width={isMobile ? 18 : 14}
                  height={isMobile ? 18 : 14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={T2}
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
                </svg>
                {!isMobile && 'Emoji'}
              </button>

              {/* Attach */}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={isUploading}
                data-testid="inbox-attach-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 0 : 5,
                  fontSize: 12,
                  color: isUploading ? T3 : T2,
                  fontFamily: 'Manrope,sans-serif',
                  background: 'none',
                  border: 'none',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                }}>
                <svg
                  width={isMobile ? 18 : 14}
                  height={isMobile ? 18 : 14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={isUploading ? T3 : T2}
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                {!isMobile && 'Attach'}
              </button>

              {/* Emoji picker */}
              {emojiOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: 0,
                    zIndex: 2000,
                  }}>
                  <Picker
                    data={data}
                    theme="dark"
                    onEmojiSelect={(e: { native: string }) => insertEmoji(e.native)}
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </div>
              )}
            </div>
            {!isMobile && <span style={{ fontSize: 12, color: T2 }}>{input.length}/1000</span>}
          </div>
        </div>
      </div>{' '}
      {/* end inner card */}
      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,application/pdf"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      {/* Close emoji on outside click */}
      {emojiOpen && (
        <div
          onClick={() => setEmojiOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1999 }}
        />
      )}
      {/* Drag overlay */}
      {isDragging && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(192,255,2,0.08)',
            border: '2px dashed rgba(192,255,2,0.4)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            pointerEvents: 'none',
          }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: ACCENT }}>Drop files to upload</span>
        </div>
      )}
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
export const ChatsPage = () => {
  const { isLeftSidebarCollapsed, setLeftSidebarCollapsed } = useDashboardLayout();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const { groupUnreads } = useUnreadMessages();
  const { mutate: setInboxState } = useSetGroupInboxState();
  const { showAlert } = useSnackbar();
  const navigate = useNavigate();

  const advisorChatEnabled = isAdvisor || canAccessAdmin || isLoadingGroups || groups.length > 0;

  useEffect(() => {
    if (!advisorChatEnabled) {
      navigate('/dashboard/home-hub', { replace: true });
    }
  }, [advisorChatEnabled, navigate]);

  if (!advisorChatEnabled) return null;

  const presenceUserIds = useMemo(
    () =>
      Array.from(
        new Set(
          groups
            .map((g) => {
              const primaryMember =
                g.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ??
                g.members[0];
              return isAdvisor ? primaryMember?.userId : g.advisorId;
            })
            .filter((id): id is string => Boolean(id)),
        ),
      ).sort(),
    [groups, isAdvisor],
  );

  const { data: presenceMap = {} } = useQuery({
    queryKey: ['presence', presenceUserIds],
    queryFn: () => getUserPresence(presenceUserIds),
    enabled: presenceUserIds.length > 0,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // For buyers: fetch each advisor's public profile (name, photo) once per unique advisorId
  const advisorIds = useMemo(
    () =>
      isAdvisor
        ? []
        : Array.from(
            new Set(groups.map((g) => g.advisorId).filter((id): id is string => Boolean(id))),
          ),
    [groups, isAdvisor],
  );
  const advisorProfileResults = useQueries({
    queries: advisorIds.map((id) => ({
      queryKey: ['public-profile', id],
      queryFn: () => getPublicUserProfile(id),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const advisorProfileMap = useMemo(() => {
    const map = new Map<string, PublicUserProfile>();
    advisorIds.forEach((id, i) => {
      const profile = advisorProfileResults[i]?.data;
      if (profile) map.set(id, profile);
    });
    return map;
  }, [advisorIds, advisorProfileResults]);

  const [activeRelId, setActiveRelId] = useState<string>('');
  const [tab, setTab] = useState('Open');
  const suppressAutoSelectRef = useRef(false);
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    setLeftSidebarCollapsed(true);
    return () => setLeftSidebarCollapsed(false);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobile) setShowList(true);
  }, [isMobile]);

  // Build conversation entries from groups
  const convos: ConvoEntry[] = useMemo(() => {
    const showArchived = tab === 'Archived';
    return groups
      .filter(
        (g) => g.status === 'active' && (showArchived ? g.isInboxArchived : !g.isInboxArchived),
      )
      .map((g) => {
        const primaryMember =
          g.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ?? g.members[0];
        const advisorProfile =
          !isAdvisor && g.advisorId ? advisorProfileMap.get(g.advisorId) : undefined;
        const otherName = isAdvisor
          ? primaryMember?.name || g.holdingName || 'Client'
          : advisorProfile?.name || advisorProfile?.firstName || 'Advisor';
        const otherEmail = isAdvisor ? primaryMember?.email : advisorProfile?.email;
        const unreadInfo = groupUnreads.find((u) => u.groupId === g.id);
        const recentMsgs = unreadInfo?.recentMessages;
        const lastMsg = recentMsgs ? recentMsgs[recentMsgs.length - 1] : undefined;
        const presenceUserId = isAdvisor ? primaryMember?.userId : g.advisorId;

        return {
          groupId: g.id,
          name: otherName,
          email: otherEmail,
          phone: isAdvisor
            ? primaryMember?.phone
              ? formatPhone(primaryMember.phone)
              : undefined
            : advisorProfile?.phoneNumber
            ? formatPhone(String(advisorProfile.phoneNumber))
            : undefined,
          photoUrl: isAdvisor ? primaryMember?.photoUrl : advisorProfile?.photoUrl,
          calLink: isAdvisor ? undefined : advisorProfile?.calLink,
          lastActiveAt: presenceUserId ? presenceMap[presenceUserId] ?? null : null,
          address: isAdvisor ? primaryMember?.address : undefined,
          city: isAdvisor ? primaryMember?.city : undefined,
          state: isAdvisor ? primaryMember?.state : undefined,
          zip: isAdvisor ? primaryMember?.zip : undefined,
          preview: lastMsg?.content?.slice(0, 40) || '',
          time: lastMsg
            ? new Date(lastMsg.createdAt).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '',
          unread: unreadInfo?.unreadCount ?? 0,
          isActive: !g.isInboxArchived,
          lastMsgAt: lastMsg?.createdAt ?? 0,
        };
      })
      .sort(
        (a, b) => b.lastMsgAt - a.lastMsgAt || b.unread - a.unread || a.name.localeCompare(b.name),
      );
  }, [groups, isAdvisor, groupUnreads, tab, presenceMap, advisorProfileMap]);

  // Auto-select first conversation (skip if we just archived/unarchived)
  useEffect(() => {
    if (!activeRelId && convos.length > 0) {
      if (suppressAutoSelectRef.current) {
        suppressAutoSelectRef.current = false;
        return;
      }
      setActiveRelId(convos[0].groupId);
    }
  }, [convos, activeRelId]);

  const handleSelect = useCallback(
    (id: string) => {
      setActiveRelId(id);
      if (isMobile) setShowList(false);
    },
    [isMobile],
  );

  const activeConvo = convos.find((c) => c.groupId === activeRelId);

  const handleArchive = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      const nextArchived = !group.isInboxArchived;
      const actionLabel = nextArchived ? 'archive' : 'unarchive';
      setInboxState(
        { groupId, isInboxArchived: nextArchived },
        {
          onSuccess: () => {
            suppressAutoSelectRef.current = true;
            if (groupId === activeRelId) setActiveRelId('');
          },
          onError: () => {
            showAlert(`Failed to ${actionLabel} conversation. Please try again.`, 'error');
          },
        },
      );
    },
    [activeRelId, groups, setInboxState, showAlert],
  );

  // All client groups for the picker (regardless of tab/status)
  const allClients: ClientPickerEntry[] = useMemo(
    () =>
      groups.map((g) => {
        const primaryMember =
          g.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ?? g.members[0];
        return {
          groupId: g.id,
          name: isAdvisor
            ? g.holdingName || primaryMember?.name || 'Client'
            : advisorProfileMap.get(g.advisorId ?? '')?.name ||
              advisorProfileMap.get(g.advisorId ?? '')?.firstName ||
              'Advisor',
          email: isAdvisor ? primaryMember?.email : undefined,
        };
      }),
    [groups, isAdvisor, advisorProfileMap],
  );

  const handlePickClient = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (group) {
        if (group.isInboxArchived) {
          setInboxState({ groupId, isInboxArchived: false });
        }
        setTab('Open');
      }
      setActiveRelId(groupId);
      if (isMobile) setShowList(false);
    },
    [groups, isMobile, setInboxState],
  );

  const showPanel = (!isMobile && isLeftSidebarCollapsed) || (isMobile && showList);
  const showChat = !isMobile || !showList;

  return (
    <div
      data-testid="chats-page"
      style={{ display: 'flex', height: '100%', minHeight: 0, overflowY: 'hidden', width: '100%' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
        @keyframes chatPanelSlideIn {
          from { transform: translateX(-16px); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }
        @keyframes chatViewSlideIn {
          from { transform: translateX(16px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .hc-input { outline: none; border: none; background: transparent; }
        .hc-input::placeholder { color: var(--tx-muted) !important; }
        .hc-scroll::-webkit-scrollbar { display: none; }
        .hc-scroll { scrollbar-width: none; }
      `}</style>

      {showPanel && (
        <ConversationPanel
          convos={convos}
          activeId={activeRelId}
          onSelect={handleSelect}
          onArchive={handleArchive}
          search={search}
          onSearch={setSearch}
          tab={tab}
          onTab={setTab}
          isMobile={isMobile}
          isAdvisor={isAdvisor}
          allClients={allClients}
          onPickClient={handlePickClient}
        />
      )}

      {showChat && activeConvo && (
        <ChatView
          groupId={activeConvo.groupId}
          name={activeConvo.name}
          email={activeConvo.email}
          phone={activeConvo.phone}
          photoUrl={activeConvo.photoUrl}
          calLink={activeConvo.calLink}
          lastActiveAt={activeConvo.lastActiveAt}
          address={activeConvo.address}
          city={activeConvo.city}
          state={activeConvo.state}
          zip={activeConvo.zip}
          isActive={activeConvo.isActive}
          isMobile={isMobile}
          panelVisible={showPanel}
          members={groups.find((g) => g.id === activeConvo.groupId)?.members}
          group={groups.find((g) => g.id === activeConvo.groupId)}
          onBack={() => setShowList(true)}
        />
      )}

      {showChat && !activeConvo && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: isMobile ? 0 : showPanel ? PANEL_W + 20 : 0,
            fontFamily: 'Manrope,sans-serif',
            color: T3,
            fontSize: 14,
          }}>
          {groups.length === 0 ? 'No conversations yet' : 'Select a conversation'}
        </div>
      )}
    </div>
  );
};
